// #region: Class definition *********************************************************************/
/**
 * Global registry for question controllers.
 * Provides centralized access to dynamically loaded controllers across views and dialogs.
 * Uses singleton pattern to preserve cache across multiple script loads.
 * 
 * The registry maintains its state even if the script is loaded multiple times,
 * ensuring that controller instances and their cached data persist across
 * different views and dialog windows.
 * 
 * Features:
 * - Controller registration and retrieval
 * - Dynamic script loading with fallback support  
 * - Form save notification system for all registered controllers
 * - Centralized form ID management across controllers
 * 
 * @example
 * // Get controller (assumes already loaded from ViewForm)
 * const controller = await questionControllerRegistry.getOrLoad('inspectionpoint');
 * 
 * @example
 * // Get controller with initialization options (common case - cache preserved)
 * const controller = await questionControllerRegistry.getOrLoad('inspectionpoint', {
 *     formId: 123,
 *     questionId: 456
 * });
 * 
 * @example
 * // With script path fallback (for direct dialog access - clear cache for fresh start)
 * const controller = await questionControllerRegistry.getOrLoad('inspectionpoint', {
 *     formId: 123,
 *     clearCache: true
 * }, '/scripts/controllers/inspectionpoint-controller.js');
 * 
 * @example
 * // Notify all controllers after form save (called from ViewForm)
 * await questionControllerRegistry.notifyControllersAfterFormSave(oldFormId, newFormId, savedData);
 * 
 * @singleton
 */
window.questionControllerRegistry = window.questionControllerRegistry || new class {

    // #region: Private Fields *******************************************************************/
    #controllers = new Map();
    #loadPromises = new Map();
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {
        // Initialize the registry if needed.
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {String} The string representation of this class.
     */
    toString() {
        return "[questionControllerRegistry]";
    }

    static toString() {
        return "[questionControllerRegistry]";
    }

    /** 
     * Identifies the version.
     * @returns {String} The version number of this class.
     */
    static version() {
        return "1.0.0";
    }

    /** 
     * Identifies the version (instance method).
     * @returns {String} The version number of this class.
     */
    version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Clears all registered controllers and load promises.
     * Useful for memory management in long-running sessions.
     */
    clear() {
        this.#controllers.clear();
        this.#loadPromises.clear();
    }

    /**
     * Gets a controller by type.
     * @param {string} controllerType - The controller type (e.g., 'inspectionpoint').
     * @returns {object|null} The controller instance or null if not registered.
     */
    get(controllerType) {
        return this.#controllers.get(controllerType) || null;
    }

    /**
     * Gets all registered controllers.
     * @returns {Map} Map of all registered controllers.
     */
    getAll() {
        return new Map(this.#controllers);
    }

    /**
     * Gets a controller with automatic fallback to dynamic loading.
     * Useful for dialogs that need access to controllers loaded in parent views.
     * @param {string} controllerType - The controller type.
     * @param {object} [initOptions] - Optional initialization parameters object.
     * @param {string} [scriptPath] - Optional path to controller script for fallback loading.
     * @returns {Promise<object|null>} Promise that resolves to controller or null.
     */
    async getOrLoad(controllerType, initOptions = {}, scriptPath = null) {
        // First try to get existing controller.
        let controller = this.get(controllerType);

        if (controller) {
            // Controller exists - initialize if it has an initialize method.
            if (typeof controller.initialize === 'function') {
                controller.initialize(initOptions);
            }

            return controller;
        }

        // Get from window global.
        const globalName = `${controllerType.replace('-', '')}Controller`;
        controller = window[globalName];

        if (controller) {
            // Register it for future use and initialize if options provided.
            this.register(controllerType, controller);
            
            // Initialize controller if it has an initialize method.
            if (typeof controller.initialize === 'function') {
                controller.initialize(initOptions);
            }

            return controller;
        }

        // Last resort - try to load dynamically if script path provided.
        if (scriptPath) {
            return await this.loadAndInitialize(controllerType, initOptions, scriptPath);
        }

        // No controller found and no script path provided.
        console.warn(`Controller '${controllerType}' not found. Make sure it's loaded or provide a script path.`);

        return null;
    }

    /**
     * Checks if a controller is registered.
     * @param {string} controllerType - The controller type.
     * @returns {boolean} True if registered, false otherwise.
     */
    has(controllerType) {
        return this.#controllers.has(controllerType);
    }

    /**
     * Loads a controller dynamically if not already loaded.
     * @param {string} controllerType - The controller type.
     * @param {string} scriptPath - Path to the controller script.
     * @returns {Promise<object>} Promise that resolves to the controller instance.
     */
    async load(controllerType, scriptPath) {
        // Return existing controller if already loaded.
        if (this.#controllers.has(controllerType)) {
            return this.#controllers.get(controllerType);
        }

        // Return existing promise if already loading.
        if (this.#loadPromises.has(controllerType)) {
            return this.#loadPromises.get(controllerType);
        }

        // Create load promise.
        const loadPromise = new Promise((resolve, reject) => {
            // Validate script path for security.
            if (!scriptPath.startsWith(window.location.origin) && !scriptPath.startsWith('/')) {
                reject(new Error(`Invalid script path: ${scriptPath}`));

                return;
            }

            const script = document.createElement('script');
            script.src = scriptPath;
            script.onload = () => {
                // Check if the controller is now available.
                const controller = this.get(controllerType);

                if (controller) {
                    this.#loadPromises.delete(controllerType);
                    resolve(controller);
                }
                else {
                    this.#loadPromises.delete(controllerType);
                    reject(new Error(`Controller ${controllerType} not found after script load`));
                }
            };
            script.onerror = () => {
                this.#loadPromises.delete(controllerType);
                reject(new Error(`Failed to load controller script: ${scriptPath}`));
            };

            document.head.appendChild(script);
        });

        this.#loadPromises.set(controllerType, loadPromise);

        return loadPromise;
    }

    /**
     * Loads a controller dynamically and ensures it's initialized.
     * @param {string} controllerType - The controller type.
     * @param {object} [initOptions] - Optional initialization parameters to pass to controller.initialize().
     * @param {string} scriptPath - Path to the controller script.
     * @returns {Promise<object>} Promise that resolves to the controller instance.
     */
    async loadAndInitialize(controllerType, initOptions = {}, scriptPath) {
        const controller = await this.load(controllerType, scriptPath);
        
        // Initialize controller if it has an initialize method.
        if (controller && typeof controller.initialize === 'function') {
            controller.initialize(initOptions);
        }

        return controller;
    }

    /**
     * Registers a controller.
     * @param {string} controllerType - The controller type.
     * @param {object} controllerInstance - The controller instance.
     */
    register(controllerType, controllerInstance) {
        this.#controllers.set(controllerType, controllerInstance);
    }

    /**
     * Unregisters a controller.
     * @param {string} controllerType - The controller type.
     */
    unregister(controllerType) {
        this.#controllers.delete(controllerType);
    }

    /**
     * Notifies all registered controllers after a form save operation.
     * Controllers can implement onAfterFormSave() to handle post-save updates.
     * @param {number|string} oldFormId - The old form ID (typically -1 for new forms).
     * @param {number|string} newFormId - The new form ID from the server.
     * @param {object} [savedData={}] - Optional saved data from server response.
     * @returns {Promise<void>} Promise that resolves when all controllers have been notified.
     */
    async notifyControllersAfterFormSave(oldFormId, newFormId, savedData = {}) {
        const notifications = [];

        for (const controller of this.#controllers.values()) {
            if (controller && typeof controller.onAfterFormSave === 'function') {
                const notification = controller.onAfterFormSave(oldFormId, newFormId, savedData);
                
                // Handle both sync and async methods.
                if (notification instanceof Promise) {
                    notifications.push(notification);
                }
            }
        }

        // Wait for all async notifications to complete.
        if (notifications.length > 0) {
            await Promise.all(notifications);
        }
    }

    /**
     * Sets the form ID on all registered controllers.
     * Used when the form ID changes (e.g., after initial save of a new form).
     * @param {number|string} formId - The new form ID.
     * @param {boolean} [clearCache=false] - Whether to clear cache on form ID change.
     */
    setFormId(formId, clearCache = false) {
        for (const controller of this.#controllers.values()) {
            if (controller && typeof controller.setFormId === 'function') {
                controller.setFormId(formId, clearCache);
            }
        }
    }
    // #endregion ********************************************************************************/

}();
// #endregion ************************************************************************************/